import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDate } from "@/lib/format";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, FunnelSimple } from "@phosphor-icons/react";

// category -> { type options, typeMode: 'select' | 'custom', typeLabel }
const CATEGORIES = {
  "Tech": { types: ["Installations", "Hosting", "Domains", "Developments", "Others"], typeLabel: "Expense Type" },
  "Office Expenses": { types: ["Salary", "Rent", "Internet Bill", "Other"], typeLabel: "Expense Type" },
  "Personal Expenses": { custom: true, typeLabel: "Description" },
  "Marketing": { types: ["Facebook Ads", "Google Ads", "YouTube"], typeLabel: "Platform" },
  "Other Expenses": { custom: true, typeLabel: "Custom Expense Name" },
};
const CAT_KEYS = Object.keys(CATEGORIES);
const empty = () => ({ category: "Tech", subcategory: "", amount: 0, date: new Date().toISOString().slice(0, 10), remarks: "", vertical: "common" });

export default function Expenses() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("Tech");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [otherType, setOtherType] = useState(false);
  const [delId, setDelId] = useState(null);
  const [flt, setFlt] = useState({ frm: "", to: "", min: "", max: "" });
  const isOther = (v) => ["other", "others"].includes(String(v || "").toLowerCase());

  const load = useCallback(async () => { try { setRows((await api.get("/expenses")).data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);

          const cfg = CATEGORIES[form.category] || {};
  const openCreate = () => { setEditing(null); setForm({ ...empty(), category: tab }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty(), ...r }); setOpen(true); };

  const submit = async () => {
    const p = { category: form.category, subcategory: form.subcategory, amount: Number(form.amount || 0), date: form.date, remarks: form.remarks, vertical: "common" };
    if (!p.amount || p.amount <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try { if (editing) await api.put(`/expenses/${editing.id}`, p); else await api.post("/expenses", p); toast.success("Expense saved"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  const doDelete = async () => { try { await api.delete(`/expenses/${delId}`); toast.success("Removed"); setOpen(false); load(); } catch { toast.error("Failed"); } finally { setDelId(null); } };

  const filtered = rows.filter((r) => {
    if (r.category !== tab) return false;
    const d = (r.date || "").slice(0, 10);
    if (flt.frm && d < flt.frm) return false;
    if (flt.to && d > flt.to) return false;
    const amt = Number(r.amount || 0);
    if (flt.min !== "" && amt < Number(flt.min)) return false;
    if (flt.max !== "" && amt > Number(flt.max)) return false;
    return true;
  });
  const filteredTotal = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div data-testid="expenses-page">
      <PageHeader overline="Expenses" title="Expenses" subtitle="Record company expenses by category. Fixed Product Cost stays on its Own Brand Sale — never entered here."
        actions={can("expenses.add") && <Button data-testid="expense-add" onClick={openCreate} className="rounded-sm bg-brand hover:bg-brand-dark"><Plus size={16} weight="bold" className="mr-1.5" /> Add Expense</Button>} />

      <div className="flex flex-wrap gap-1 mb-4 bg-white border border-zinc-200 rounded-sm p-1 w-fit">
        {CAT_KEYS.map((c) => (
          <button key={c} data-testid={`expense-tab-${c.replace(/\s/g, "-")}`} onClick={() => setTab(c)}
            className={`px-4 py-2 text-sm rounded-sm transition-colors ${tab === c ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>{c}</button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1"><FunnelSimple size={12} /> From</Label><Input data-testid="expense-filter-from" type="date" value={flt.frm} onChange={(e) => setFlt({ ...flt, frm: e.target.value })} className="h-9 rounded-sm w-40" /></div>
        <div><Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 block">To</Label><Input data-testid="expense-filter-to" type="date" value={flt.to} onChange={(e) => setFlt({ ...flt, to: e.target.value })} className="h-9 rounded-sm w-40" /></div>
        <div><Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 block">Min ₹</Label><Input data-testid="expense-filter-min" type="number" value={flt.min} onChange={(e) => setFlt({ ...flt, min: e.target.value })} className="h-9 rounded-sm w-28" placeholder="0" /></div>
        <div><Label className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 block">Max ₹</Label><Input data-testid="expense-filter-max" type="number" value={flt.max} onChange={(e) => setFlt({ ...flt, max: e.target.value })} className="h-9 rounded-sm w-28" placeholder="∞" /></div>
        {(flt.frm || flt.to || flt.min !== "" || flt.max !== "") && <Button variant="ghost" size="sm" className="rounded-sm h-9 text-zinc-500" data-testid="expense-filter-clear" onClick={() => setFlt({ frm: "", to: "", min: "", max: "" })}>Clear</Button>}
        <div className="ml-auto text-sm text-zinc-500" data-testid="expense-filter-total">{filtered.length} entries · <span className="font-medium text-zinc-900 tabular-nums">{inr(filteredTotal)}</span></div>
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-zinc-50 hover:bg-zinc-50">
            {["Date", CATEGORIES[tab]?.typeLabel || "Type", "Amount", "Remarks", "Actions"].map((h) => (
              <TableHead key={h} className={`text-[11px] uppercase tracking-wider text-zinc-500 font-medium ${h === "Actions" ? "text-right" : ""}`}>{h}</TableHead>
            ))}
          </TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-12 text-zinc-400">No {tab} entries yet.</TableCell></TableRow>
              : filtered.map((r) => (
                <TableRow key={r.id} data-testid="expense-row" className="border-zinc-100 hover:bg-zinc-50">
                  <TableCell className="text-sm text-zinc-600 whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-sm font-medium text-zinc-900">{r.subcategory || "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.amount)}</TableCell>
                  <TableCell className="text-sm text-zinc-500 max-w-xs truncate">{r.remarks || "—"}</TableCell>
                  <TableCell className="text-right">
                    {can("expenses.edit") && <Button data-testid="expense-edit" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-zinc-500 hover:text-zinc-900" onClick={() => openEdit(r)}><PencilSimple size={16} /></Button>}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editing ? "Edit Expense" : "Add Expense"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div><Label className="text-xs text-zinc-500 mb-1.5 block">Category</Label>
              <Select value={form.category} onValueChange={(v) => { setForm({ ...form, category: v, subcategory: "" }); setOtherType(false); }}>
                <SelectTrigger data-testid="expense-category" className="rounded-sm h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-sm">{CAT_KEYS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-zinc-500 mb-1.5 block">Date</Label><Input data-testid="expense-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm h-10" /></div>
            <div className="col-span-2">
              <Label className="text-xs text-zinc-500 mb-1.5 block">{cfg.typeLabel || "Type"}</Label>
              {cfg.custom ? (
                <Input data-testid="expense-subcategory" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="rounded-sm h-10" placeholder={cfg.typeLabel} />
              ) : (() => {
                const otherOpt = (cfg.types || []).find((t) => isOther(t));
                return (
                  <>
                    <Select value={otherType && otherOpt ? otherOpt : (form.subcategory || "")} onValueChange={(v) => { if (isOther(v)) { setOtherType(true); setForm((f) => ({ ...f, subcategory: "" })); } else { setOtherType(false); setForm((f) => ({ ...f, subcategory: v })); } }}>
                      <SelectTrigger data-testid="expense-subcategory" className="rounded-sm h-10"><SelectValue placeholder={`Select ${cfg.typeLabel?.toLowerCase()}…`} /></SelectTrigger>
                      <SelectContent className="rounded-sm">{(cfg.types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                    {otherType && <Input data-testid="expense-subcategory-custom" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="rounded-sm h-10 mt-2" placeholder="Enter custom type" />}
                  </>
                );
              })()}
            </div>
            <div className="col-span-2"><Label className="text-xs text-zinc-500 mb-1.5 block">Amount (₹)</Label><Input data-testid="expense-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm h-10" /></div>
            <div className="col-span-2"><Label className="text-xs text-zinc-500 mb-1.5 block">Remarks</Label><Textarea data-testid="expense-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="rounded-sm" /></div>
          </div>
          <DialogFooter>{editing && can("expenses.delete") && <Button data-testid="expense-delete" variant="outline" className="rounded-sm mr-auto text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => setDelId(editing.id)}><Trash size={16} className="mr-1.5" /> Delete</Button>}<Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="expense-submit" onClick={submit} disabled={saving} className="rounded-sm bg-brand hover:bg-brand-dark">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>Remove this expense?</AlertDialogTitle><AlertDialogDescription>This permanently removes the expense entry.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-expense-delete" onClick={doDelete} className="rounded-sm bg-red-600 hover:bg-red-700">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
